/* eslint-disable react/prop-types */
import React from "react";
import PropTypes from "prop-types";
import withStyles from "@material-ui/core/styles/withStyles";
import Modal from "@material-ui/core/Modal";
import Box from "@material-ui/core/Box";
import Chip from "@material-ui/core/Chip";
import TextField from "@material-ui/core/TextField";
import Paper from "@material-ui/core/Paper";
import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import ListItemText from "@material-ui/core/ListItemText";
import CircularProgress from "@material-ui/core/CircularProgress";
import Divider from "@material-ui/core/Divider";
import InputAdornment from "@material-ui/core/InputAdornment";
import Button from "components/CustomButtons/Button.jsx";
import GridContainer from "components/Grid/GridContainer.jsx";
import GridItem from "components/Grid/GridItem.jsx";
import Card from "components/Card/Card.jsx";
import CardHeader from "components/Card/CardHeader.jsx";
import CardBody from "components/Card/CardBody.jsx";
import CardFooter from "components/Card/CardFooter.jsx";
import SnackbarContent from "components/Snackbar/SnackbarContent.jsx";
import {
  createUserGroup,
  getUserGroups,
  inviteUsersToGroup,
  searchUsers
} from "services/groupMissionsDataProvider.js";
import {
  prefersDarkModeMediaQuery,
  darkModeStyleBlocks
} from "assets/jss/material-dashboard-react.jsx";

const styles = {
  headerRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "12px",
    flexWrap: "wrap"
  },
  cardTitle: {
    margin: 0,
    fontWeight: 600
  },
  cardSubtitle: {
    marginTop: "6px",
    opacity: 0.85
  },
  bodyText: {
    color: "#334155"
  },
  sectionLabel: {
    marginTop: "14px",
    marginBottom: "8px",
    fontWeight: 600,
    color: "#235894"
  },
  chipRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: "8px"
  },
  emptyState: {
    padding: "24px",
    textAlign: "center",
    color: "#67748a"
  },
  modal: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "rgba(15, 23, 42, 0.48)",
    padding: "16px"
  },
  modalBox: {
    backgroundColor: "#fff",
    borderRadius: "12px",
    width: "100%",
    maxWidth: "680px",
    maxHeight: "calc(100vh - 32px)",
    overflowY: "auto",
    padding: "24px",
    boxShadow: "0 24px 80px rgba(15, 23, 42, 0.22)"
  },
  modalTitle: {
    marginTop: 0,
    marginBottom: "18px"
  },
  formField: {
    marginBottom: "16px"
  },
  helperText: {
    marginTop: "-6px",
    marginBottom: "12px",
    color: "#67748a",
    fontSize: "12px"
  },
  modalActions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: "8px",
    marginTop: "8px"
  },
  footerActions: {
    width: "100%",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "8px"
  },
  metaText: {
    color: "#67748a",
    fontSize: "13px"
  },
  selectedUsersRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: "6px",
    minHeight: "32px",
    marginBottom: "10px",
    alignItems: "center"
  },
  searchDropdown: {
    border: "1px solid #e0e0e0",
    borderRadius: "4px",
    marginTop: "2px",
    marginBottom: "14px",
    maxHeight: "200px",
    overflowY: "auto",
    backgroundColor: "#fff",
    "& .MuiListItemText-primary": {
      color: "#0f172a"
    },
    "& .MuiListItemText-secondary": {
      color: "#64748b"
    }
  },
  searchNoResult: {
    padding: "10px 16px",
    color: "#67748a",
    fontSize: "13px"
  },
  [prefersDarkModeMediaQuery]: darkModeStyleBlocks.groups
};

const defaultGroupForm = {
  name: "",
  group_type: "",
  description: ""
};

class Groups extends React.Component {
  constructor(props) {
    super(props);
    this._searchTimer = null;
    this.state = {
      groups: [],
      loading: true,
      createOpen: false,
      inviteOpen: false,
      activeGroup: null,
      formData: { ...defaultGroupForm },
      createSelectedUsers: [],
      inviteSelectedUsers: [],
      userSearchQuery: "",
      userSearchResults: [],
      userSearchLoading: false,
      saving: false,
      error: "",
      success: ""
    };
  }

  componentDidMount() {
    this.loadGroups();
  }

  loadGroups = async () => {
    try {
      this.setState({ loading: true, error: "" });
      const response = await getUserGroups(this.props.axios);
      this.setState({
        groups: response.results || response || [],
        loading: false
      });
    } catch (error) {
      this.setState({
        loading: false,
        error: "Failed to load groups."
      });
    }
  };

  openCreateModal = () => {
    this.setState({
      createOpen: true,
      formData: { ...defaultGroupForm },
      createSelectedUsers: [],
      userSearchQuery: "",
      userSearchResults: [],
      error: "",
      success: ""
    });
  };

  closeCreateModal = () => {
    clearTimeout(this._searchTimer);
    this.setState({
      createOpen: false,
      formData: { ...defaultGroupForm },
      createSelectedUsers: [],
      userSearchQuery: "",
      userSearchResults: []
    });
  };

  openInviteModal = group => {
    this.setState({
      inviteOpen: true,
      activeGroup: group,
      inviteSelectedUsers: [],
      userSearchQuery: "",
      userSearchResults: [],
      error: "",
      success: ""
    });
  };

  closeInviteModal = () => {
    clearTimeout(this._searchTimer);
    this.setState({
      inviteOpen: false,
      activeGroup: null,
      inviteSelectedUsers: [],
      userSearchQuery: "",
      userSearchResults: []
    });
  };

  handleFormChange = field => event => {
    this.setState({
      formData: {
        ...this.state.formData,
        [field]: event.target.value
      }
    });
  };

  handleUserSearch = (query, context) => {
    this.setState({ userSearchQuery: query, userSearchResults: [] });
    clearTimeout(this._searchTimer);
    if (query.length < 2) {
      this.setState({ userSearchLoading: false });
      return;
    }
    this.setState({ userSearchLoading: true });
    this._searchTimer = setTimeout(async () => {
      try {
        const results = await searchUsers(this.props.axios, query);
        // Filter out already selected users
        const selectedIds = new Set(
          (context === "create"
            ? this.state.createSelectedUsers
            : this.state.inviteSelectedUsers
          ).map(u => u.id)
        );
        this.setState({
          userSearchResults: (results || []).filter(
            u => !selectedIds.has(u.id)
          ),
          userSearchLoading: false
        });
      } catch (_) {
        this.setState({ userSearchLoading: false });
      }
    }, 300);
  };

  selectUser = (user, context) => {
    const field =
      context === "create" ? "createSelectedUsers" : "inviteSelectedUsers";
    const already = this.state[field].some(u => u.id === user.id);
    if (already) return;
    this.setState(state => ({
      [field]: [...state[field], user],
      userSearchQuery: "",
      userSearchResults: []
    }));
  };

  removeSelectedUser = (userId, context) => {
    const field =
      context === "create" ? "createSelectedUsers" : "inviteSelectedUsers";
    this.setState(state => ({
      [field]: state[field].filter(u => u.id !== userId)
    }));
  };

  renderUserSearch = context => {
    const { classes } = this.props;
    const {
      userSearchQuery,
      userSearchResults,
      userSearchLoading
    } = this.state;
    const selectedUsers =
      context === "create"
        ? this.state.createSelectedUsers
        : this.state.inviteSelectedUsers;

    return (
      <div>
        <div className={classes.selectedUsersRow}>
          {selectedUsers.length === 0 ? (
            <span className={classes.metaText}>No users selected yet.</span>
          ) : (
            selectedUsers.map(user => (
              <Chip
                key={`sel-${user.id}`}
                label={user.username}
                size="small"
                onDelete={() => this.removeSelectedUser(user.id, context)}
              />
            ))
          )}
        </div>
        <TextField
          className={classes.formField}
          fullWidth
          label="Search users"
          placeholder="Search by name, username or email"
          value={userSearchQuery}
          onChange={e => this.handleUserSearch(e.target.value, context)}
          InputProps={{
            endAdornment: userSearchLoading ? (
              <InputAdornment position="end">
                <CircularProgress size={16} />
              </InputAdornment>
            ) : null
          }}
        />
        {userSearchResults.length > 0 && (
          <Paper className={classes.searchDropdown} elevation={2}>
            <List dense disablePadding>
              {userSearchResults.map((user, idx) => {
                const displayName =
                  `${user.first_name || ""} ${user.last_name || ""}`.trim() ||
                  user.email;
                return (
                  <React.Fragment key={user.id}>
                    {idx > 0 && <Divider />}
                    <ListItem
                      button
                      onClick={() => this.selectUser(user, context)}
                    >
                      <ListItemText
                        primary={user.username}
                        secondary={displayName}
                      />
                    </ListItem>
                  </React.Fragment>
                );
              })}
            </List>
          </Paper>
        )}
        {userSearchQuery.length >= 2 &&
          !userSearchLoading &&
          userSearchResults.length === 0 && (
            <div className={classes.searchNoResult}>
              No matching users found.
            </div>
          )}
      </div>
    );
  };

  submitCreateGroup = async () => {
    const { formData } = this.state;
    if (!formData.name.trim()) {
      this.setState({ error: "Group name is required." });
      return;
    }

    try {
      this.setState({ saving: true, error: "", success: "" });
      await createUserGroup(this.props.axios, {
        name: formData.name.trim(),
        group_type: formData.group_type.trim() || null,
        description: formData.description.trim() || null,
        invite_user_ids: this.state.createSelectedUsers.map(u => u.id)
      });
      this.setState({
        saving: false,
        success: "Group created successfully."
      });
      this.closeCreateModal();
      this.loadGroups();
    } catch (error) {
      this.setState({
        saving: false,
        error: "Failed to create group."
      });
    }
  };

  submitInvites = async () => {
    const { activeGroup } = this.state;
    if (!activeGroup) {
      return;
    }
    try {
      this.setState({ saving: true, error: "", success: "" });
      await inviteUsersToGroup(this.props.axios, activeGroup.id, {
        invite_user_ids: this.state.inviteSelectedUsers.map(u => u.id)
      });
      this.setState({
        saving: false,
        success: "Invites processed successfully."
      });
      this.closeInviteModal();
      this.loadGroups();
    } catch (error) {
      this.setState({
        saving: false,
        error: "Failed to invite users to the group."
      });
    }
  };

  renderMembers = members => {
    const { classes } = this.props;
    if (!members || !members.length) {
      return <div className={classes.metaText}>No members yet.</div>;
    }
    return (
      <div className={classes.chipRow}>
        {members.map(member => (
          <Chip
            key={`member-${member.id}`}
            label={member.username}
            size="small"
          />
        ))}
      </div>
    );
  };

  renderMissingInvites = missingInvites => {
    const { classes } = this.props;
    if (!missingInvites || !missingInvites.length) {
      return (
        <div className={classes.metaText}>All invite targets resolved.</div>
      );
    }
    return (
      <div className={classes.chipRow}>
        {missingInvites.map((entry, index) => (
          <Chip
            key={`missing-${entry.type}-${entry.value}-${index}`}
            label={`${entry.type}: ${entry.value}`}
            size="small"
            variant="outlined"
          />
        ))}
      </div>
    );
  };

  render() {
    const { classes } = this.props;
    const {
      groups,
      loading,
      createOpen,
      inviteOpen,
      activeGroup,
      formData,
      saving,
      error,
      success
    } = this.state;

    return (
      <GridContainer>
        <GridItem xs={12} sm={12} md={12}>
          {error ? <SnackbarContent message={error} color="danger" /> : null}
          {success ? (
            <SnackbarContent message={success} color="success" />
          ) : null}
          <Card>
            <CardHeader color="info">
              <div className={classes.headerRow}>
                <div>
                  <h4 className={classes.cardTitle}>Groups</h4>
                  <p className={classes.cardSubtitle}>
                    Create user groups and send invites to existing users.
                  </p>
                </div>
                <Button color="warning" onClick={this.openCreateModal}>
                  Create Group
                </Button>
              </div>
            </CardHeader>
            <CardBody>
              {loading ? (
                <div className={classes.emptyState}>Loading groups...</div>
              ) : groups.length ? (
                <GridContainer>
                  {groups.map(group => (
                    <GridItem xs={12} sm={12} md={6} key={`group-${group.id}`}>
                      <Card>
                        <CardBody>
                          <h4 className={classes.cardTitle}>{group.name}</h4>
                          <div className={classes.metaText}>
                            {group.group_type || "General group"}
                          </div>
                          <p className={classes.bodyText}>
                            {group.description || "No description provided."}
                          </p>

                          <div className={classes.sectionLabel}>Members</div>
                          {this.renderMembers(group.members)}

                          <div className={classes.sectionLabel}>
                            Pending / Missing Invites
                          </div>
                          {this.renderMissingInvites(group.missing_invites)}
                        </CardBody>
                        <CardFooter>
                          <div className={classes.footerActions}>
                            <div className={classes.metaText}>
                              {group.members ? group.members.length : 0} members
                            </div>
                            <Button
                              color="info"
                              size="sm"
                              onClick={() => this.openInviteModal(group)}
                            >
                              Invite Users
                            </Button>
                          </div>
                        </CardFooter>
                      </Card>
                    </GridItem>
                  ))}
                </GridContainer>
              ) : (
                <div className={classes.emptyState}>
                  No groups yet. Create the first one to start missions and
                  challenges.
                </div>
              )}
            </CardBody>
          </Card>
        </GridItem>

        <Modal
          className={classes.modal}
          open={createOpen}
          onClose={this.closeCreateModal}
        >
          <Box className={classes.modalBox}>
            <h3 className={classes.modalTitle}>Create Group</h3>
            <TextField
              className={classes.formField}
              fullWidth
              label="Group name"
              value={formData.name}
              onChange={this.handleFormChange("name")}
            />
            <TextField
              className={classes.formField}
              fullWidth
              label="Group type"
              value={formData.group_type}
              onChange={this.handleFormChange("group_type")}
            />
            <TextField
              className={classes.formField}
              fullWidth
              multiline
              rows={3}
              label="Description"
              value={formData.description}
              onChange={this.handleFormChange("description")}
            />
            <div className={classes.sectionLabel}>Invite Members</div>
            {this.renderUserSearch("create")}
            <div className={classes.modalActions}>
              <Button color="transparent" onClick={this.closeCreateModal}>
                Cancel
              </Button>
              <Button
                color="info"
                onClick={this.submitCreateGroup}
                disabled={saving}
              >
                {saving ? "Saving..." : "Create Group"}
              </Button>
            </div>
          </Box>
        </Modal>

        <Modal
          className={classes.modal}
          open={inviteOpen}
          onClose={this.closeInviteModal}
        >
          <Box className={classes.modalBox}>
            <h3 className={classes.modalTitle}>
              Invite Users
              {activeGroup ? ` to ${activeGroup.name}` : ""}
            </h3>
            {this.renderUserSearch("invite")}
            <div className={classes.modalActions}>
              <Button color="transparent" onClick={this.closeInviteModal}>
                Cancel
              </Button>
              <Button
                color="info"
                onClick={this.submitInvites}
                disabled={saving}
              >
                {saving ? "Sending..." : "Send Invites"}
              </Button>
            </div>
          </Box>
        </Modal>
      </GridContainer>
    );
  }
}

Groups.propTypes = {
  axios: PropTypes.object.isRequired,
  classes: PropTypes.object.isRequired
};

export default withStyles(styles)(Groups);
